/**
 * Firebase Service for GOATnote EHR
 * Manages Firestore connection and provides data access methods
 * Enhanced with emergency bypass for authentication issues
 */

import { 
  collection, 
  doc, 
  getDocs, 
  getDoc,
  setDoc,
  updateDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  writeBatch,
  enableNetwork,
  disableNetwork
} from 'firebase/firestore';
import { db } from '../config/firebase.config';
import { COLLECTIONS } from '../config/firebase.config';

// Bed Status Constants - Following best practices for string enums
export const BED_STATUS = {
  AVAILABLE: 'available',
  OCCUPIED: 'occupied',
  CLEANING: 'cleaning',
  MAINTENANCE: 'maintenance',
  RESERVED: 'reserved'
};

// Emergency fallback data for when Firestore fails
const EMERGENCY_FALLBACK_DATA = {
  beds: [
    // Emergency Department beds
    { id: 'ed-001', zone: 'emergency', number: 'ED-1', status: 'available', capacity: 1 },
    { id: 'ed-002', zone: 'emergency', number: 'ED-2', status: 'occupied', capacity: 1, patientName: 'John Doe', esiLevel: 2 },
    { id: 'ed-003', zone: 'emergency', number: 'ED-3', status: 'available', capacity: 1 },
    { id: 'ed-004', zone: 'emergency', number: 'ED-4', status: 'occupied', capacity: 1, patientName: 'Jane Smith', esiLevel: 3 },
    { id: 'ed-005', zone: 'emergency', number: 'ED-5', status: 'cleaning', capacity: 1 },
    // ICU beds
    { id: 'icu-001', zone: 'icu', number: 'ICU-1', status: 'occupied', capacity: 1, patientName: 'Critical Patient', esiLevel: 1 },
    { id: 'icu-002', zone: 'icu', number: 'ICU-2', status: 'available', capacity: 1 },
    { id: 'icu-003', zone: 'icu', number: 'ICU-3', status: 'maintenance', capacity: 1 },
    // General Ward beds
    { id: 'gen-001', zone: 'general', number: 'GEN-1', status: 'occupied', capacity: 1, patientName: 'Recovery Patient', esiLevel: 4 },
    { id: 'gen-002', zone: 'general', number: 'GEN-2', status: 'available', capacity: 1 },
    { id: 'gen-003', zone: 'general', number: 'GEN-3', status: 'available', capacity: 1 }
  ],
  zones: [
    { id: 'emergency', name: 'Emergency Department', capacity: 50, description: 'Emergency room beds for acute care' },
    { id: 'icu', name: 'Intensive Care Unit', capacity: 20, description: 'Critical care beds with advanced monitoring' },
    { id: 'general', name: 'General Ward', capacity: 100, description: 'Standard hospital beds' },
    { id: 'pediatric', name: 'Pediatric Ward', capacity: 30, description: 'Beds for pediatric patients' },
    { id: 'observation', name: 'Observation Unit', capacity: 25, description: 'Short-stay observation beds' }
  ],
  alerts: []
};

// Enhanced error handling for Firestore operations
const handleFirestoreError = (error, operation) => {
  console.error(`🚨 Firestore ${operation} failed:`, error);
  
  // Check for common authentication/permission errors
  if (error.code === 'permission-denied' || 
      error.code === 'unauthenticated' ||
      error.message.includes('Missing or insufficient permissions')) {
    console.warn('🔒 Firestore security rules blocking access - using emergency fallback data');
    return { usesFallback: true, error: 'Authentication/Permission Error' };
  }
  
  // Check for network errors
  if (error.code === 'unavailable' || 
      error.code === 'deadline-exceeded' ||
      error.message.includes('network')) {
    console.warn('🌐 Firestore network error - using emergency fallback data');
    return { usesFallback: true, error: 'Network Error' };
  }
  
  // Other errors
  return { usesFallback: true, error: error.message };
};

// Enable offline persistence (commented out for now - requires additional setup)
// enableIndexedDbPersistence(db).catch((err) => {
//   if (err.code === 'failed-precondition') {
//     console.warn('Firestore persistence failed: Multiple tabs open');
//   } else if (err.code === 'unimplemented') {
//     console.warn('Firestore persistence not available in this browser');
//   }
// });

/**
 * Bed Management Functions
 */

// Get all beds with optional filters
export const getBeds = async (filters = {}) => {
  try {
    let q = collection(db, COLLECTIONS.BEDS);
    
    if (filters.zone) {
      q = query(q, where('zone', '==', filters.zone));
    }
    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }
    if (filters.orderBy) {
      q = query(q, orderBy(filters.orderBy, filters.orderDirection || 'asc'));
    }
    
    const snapshot = await getDocs(q);
    const beds = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    console.log('✅ Firestore beds loaded successfully:', beds.length);
    return beds;
  } catch (error) {
    const errorResult = handleFirestoreError(error, 'getBeds');
    if (errorResult.usesFallback) {
      console.log('🚨 Using emergency fallback bed data');
      let fallbackBeds = [...EMERGENCY_FALLBACK_DATA.beds];
      
      // Apply filters to fallback data
      if (filters.zone) {
        fallbackBeds = fallbackBeds.filter(bed => bed.zone === filters.zone);
      }
      if (filters.status) {
        fallbackBeds = fallbackBeds.filter(bed => bed.status === filters.status);
      }
      
      return fallbackBeds;
    }
    throw error;
  }
};

// Get single bed by ID
export const getBedById = async (bedId) => {
  try {
    const bedDoc = await getDoc(doc(db, COLLECTIONS.BEDS, bedId));
    if (bedDoc.exists()) {
      return { id: bedDoc.id, ...bedDoc.data() };
    }
    return null;
  } catch (error) {
    const errorResult = handleFirestoreError(error, 'getBedById');
    if (errorResult.usesFallback) {
      const fallbackBed = EMERGENCY_FALLBACK_DATA.beds.find(bed => bed.id === bedId);
      return fallbackBed || null;
    }
    throw error;
  }
};

// Update bed status
export const updateBedStatus = async (bedId, updates) => {
  try {
    const bedRef = doc(db, COLLECTIONS.BEDS, bedId);
    await updateDoc(bedRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    const errorResult = handleFirestoreError(error, 'updateBedStatus');
    if (errorResult.usesFallback) {
      console.log('🚨 Firestore write failed - simulating bed update');
      // In emergency mode, log the action but can't persist
      return true;
    }
    throw error;
  }
};

// Assign patient to bed
export const assignPatientToBed = async (bedId, patientData) => {
  try {
    const bedRef = doc(db, COLLECTIONS.BEDS, bedId);
    await updateDoc(bedRef, {
      status: BED_STATUS.OCCUPIED,
      patientId: patientData.patientId,
      patientName: patientData.patientName,
      encounterId: patientData.encounterId,
      esiLevel: patientData.esiLevel,
      assignedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    // Create audit log
    await createAuditLog({
      action: 'BED_ASSIGNED',
      bedId,
      patientId: patientData.patientId,
      userId: patientData.assignedBy
    });
    
    return true;
  } catch (error) {
    const errorResult = handleFirestoreError(error, 'assignPatientToBed');
    if (errorResult.usesFallback) {
      console.log('🚨 Firestore write failed - simulating patient assignment');
      return true;
    }
    throw error;
  }
};

// Release bed
export const releaseBed = async (bedId, userId) => {
  try {
    const bedRef = doc(db, COLLECTIONS.BEDS, bedId);
    const bedData = await getDoc(bedRef);
    
    if (bedData.exists()) {
      const previousPatientId = bedData.data().patientId;
      
      await updateDoc(bedRef, {
        status: BED_STATUS.CLEANING,
        patientId: null,
        patientName: null,
        encounterId: null,
        esiLevel: null,
        assignedAt: null,
        releasedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      // Create audit log
      await createAuditLog({
        action: 'BED_RELEASED',
        bedId,
        previousPatientId,
        userId
      });
      
      return true;
    }
    return false;
  } catch (error) {
    const errorResult = handleFirestoreError(error, 'releaseBed');
    if (errorResult.usesFallback) {
      console.log('🚨 Firestore write failed - simulating bed release');
      return true;
    }
    throw error;
  }
};

/**
 * Zone Management Functions
 */

// Get all zones
export const getZones = async () => {
  try {
    const snapshot = await getDocs(collection(db, COLLECTIONS.ZONES));
    const zones = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    console.log('✅ Firestore zones loaded successfully:', zones.length);
    return zones;
  } catch (error) {
    const errorResult = handleFirestoreError(error, 'getZones');
    if (errorResult.usesFallback) {
      console.log('🚨 Using emergency fallback zone data');
      return [...EMERGENCY_FALLBACK_DATA.zones];
    }
    throw error;
  }
};

/**
 * Alert Management Functions
 */

// Create alert
export const createAlert = async (alertData) => {
  try {
    const alertRef = doc(collection(db, COLLECTIONS.ALERTS));
    await setDoc(alertRef, {
      ...alertData,
      acknowledged: false,
      createdAt: serverTimestamp()
    });
    return alertRef.id;
  } catch (error) {
    const errorResult = handleFirestoreError(error, 'createAlert');
    if (errorResult.usesFallback) {
      console.log('🚨 Firestore write failed - alert logged locally only');
      return `fallback-${Date.now()}`;
    }
    throw error;
  }
};

// Get unacknowledged alerts
export const getUnacknowledgedAlerts = async (limitCount = 10) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.ALERTS),
      where('acknowledged', '==', false),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    const errorResult = handleFirestoreError(error, 'getUnacknowledgedAlerts');
    if (errorResult.usesFallback) {
      console.log('🚨 Using emergency fallback alert data');
      return [...EMERGENCY_FALLBACK_DATA.alerts];
    }
    throw error;
  }
};

/**
 * Real-time Subscriptions with Fallback
 */

// Subscribe to bed changes
export const subscribeToBeds = (callback, filters = {}) => {
  let q = collection(db, COLLECTIONS.BEDS);
  
  if (filters.zone) {
    q = query(q, where('zone', '==', filters.zone));
  }
  if (filters.status) {
    q = query(q, where('status', '==', filters.status));
  }
  
  return onSnapshot(q, (snapshot) => {
    const beds = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    console.log('🔄 Real-time beds update:', beds.length);
    callback(beds);
  }, (error) => {
    console.error('🚨 Error in beds subscription:', error);
    const errorResult = handleFirestoreError(error, 'subscribeToBeds');
    if (errorResult.usesFallback) {
      console.log('🚨 Real-time subscription failed - using fallback data');
      let fallbackBeds = [...EMERGENCY_FALLBACK_DATA.beds];
      
      // Apply filters to fallback data
      if (filters.zone) {
        fallbackBeds = fallbackBeds.filter(bed => bed.zone === filters.zone);
      }
      if (filters.status) {
        fallbackBeds = fallbackBeds.filter(bed => bed.status === filters.status);
      }
      
      callback(fallbackBeds);
    }
  });
};

// Subscribe to single bed changes
export const subscribeToBed = (bedId, callback) => {
  return onSnapshot(doc(db, COLLECTIONS.BEDS, bedId), (doc) => {
    if (doc.exists()) {
      callback({ id: doc.id, ...doc.data() });
    } else {
      callback(null);
    }
  }, (error) => {
    console.error('🚨 Error in bed subscription:', error);
    const errorResult = handleFirestoreError(error, 'subscribeToBed');
    if (errorResult.usesFallback) {
      const fallbackBed = EMERGENCY_FALLBACK_DATA.beds.find(bed => bed.id === bedId);
      callback(fallbackBed || null);
    }
  });
};

// Subscribe to alerts
export const subscribeToAlerts = (callback) => {
  const q = query(
    collection(db, COLLECTIONS.ALERTS),
    where('acknowledged', '==', false),
    orderBy('createdAt', 'desc'),
    limit(10)
  );
  
  return onSnapshot(q, (snapshot) => {
    const alerts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(alerts);
  }, (error) => {
    console.error('🚨 Error in alerts subscription:', error);
    const errorResult = handleFirestoreError(error, 'subscribeToAlerts');
    if (errorResult.usesFallback) {
      callback([...EMERGENCY_FALLBACK_DATA.alerts]);
    }
  });
};

/**
 * Utility Functions
 */

// Create audit log
const createAuditLog = async (logData) => {
  try {
    const auditRef = doc(collection(db, COLLECTIONS.AUDIT));
    await setDoc(auditRef, {
      ...logData,
      timestamp: serverTimestamp(),
      userAgent: navigator.userAgent
    });
  } catch (error) {
    console.error('🚨 Error creating audit log:', error);
    // Don't throw - audit logging should not break main functionality
  }
};

// Batch update multiple beds
export const batchUpdateBeds = async (updates) => {
  try {
    const batch = writeBatch(db);
    
    updates.forEach(({ bedId, data }) => {
      const bedRef = doc(db, COLLECTIONS.BEDS, bedId);
      batch.update(bedRef, {
        ...data,
        updatedAt: serverTimestamp()
      });
    });
    
    await batch.commit();
    return true;
  } catch (error) {
    const errorResult = handleFirestoreError(error, 'batchUpdateBeds');
    if (errorResult.usesFallback) {
      console.log('🚨 Firestore batch write failed - simulating updates');
      return true;
    }
    throw error;
  }
};

// Network management
export const goOffline = () => disableNetwork(db);
export const goOnline = () => enableNetwork(db);

// Export Firestore timestamp helper
export { serverTimestamp }; 